import { useState, useEffect, useCallback } from 'react';

const API = `${window.location.protocol}//${window.location.hostname}/api`;
function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || 'Request failed');
    return d;
  });
}

const MANAGER = new Set(['admin', 'rpm', 'field_manager', 'director', 'managing_director', 'management']);

const STATUS_BADGE = {
  in_transit: { label: 'In Transit', bg: '#1e3a5f', fg: '#93c5fd' },
  delivered:  { label: 'Delivered · Verify', bg: '#3a2e0a', fg: '#fbbf24' },
  received:   { label: 'Received', bg: '#0d2818', fg: '#4ade80' },
};

const inputStyle = {
  padding: '7px 9px', backgroundColor: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box', width: '100%',
};
const label = { color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 };

export default function MaterialPanel({ workItemId, role }) {
  const canManage = MANAGER.has(role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Shipment + receive state
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ po_number: '', tracking_number: '', carrier: '', items: [{ description: '', expected_qty: '', unit: '' }] });
  const [templates, setTemplates] = useState([]);
  const [receiving, setReceiving] = useState(null);
  const [recvItems, setRecvItems] = useState([]);
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);

  // Borrow state
  const [showBorrow, setShowBorrow] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [borrowFrom, setBorrowFrom] = useState('');
  const [avail, setAvail] = useState(null);            // null=not loaded, []=none, [...]=items
  const [picks, setPicks] = useState({});              // description -> { checked, qty }
  const [borrowReason, setBorrowReason] = useState('');
  const [confirmBorrow, setConfirmBorrow] = useState(false);
  const [selBorrows, setSelBorrows] = useState([]);          // borrow ids selected to replenish
  const [showReplenish, setShowReplenish] = useState(false);
  const [replForm, setReplForm] = useState({ po_number: '', tracking_number: '' });

  const load = useCallback(() => {
    setLoading(true);
    api(`/materials/work-item/${workItemId}`)
      .then(setData).catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, [workItemId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canManage) api('/materials/templates').then(setTemplates).catch(() => {}); }, [canManage]);

  // ── Shipments ──────────────────────────────────────────────
  const submitShipment = async () => {
    setBusy(true); setErr('');
    try {
      await api('/materials', {
        method: 'POST',
        body: JSON.stringify({
          work_item_id: workItemId,
          po_number: form.po_number || null,
          tracking_number: form.tracking_number || null,
          carrier: form.carrier || null,
          items: form.items.filter(i => i.description.trim()).map(i => ({
            description: i.description, expected_qty: Number(i.expected_qty) || 0, unit: i.unit || null,
          })),
        }),
      });
      setShowAdd(false);
      setForm({ po_number: '', tracking_number: '', carrier: '', items: [{ description: '', expected_qty: '', unit: '' }] });
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const applyTemplate = (tplId) => {
    const tpl = templates.find(t => String(t.id) === String(tplId));
    if (!tpl) return;
    setForm(f => ({ ...f, items: (tpl.items || []).map(i => ({ description: i.description, expected_qty: i.qty, unit: i.unit || '' })) }));
  };
  const saveTemplate = async () => {
    const name = window.prompt('Name this pick list (e.g. "Router EOL — standard"):');
    if (!name) return;
    setBusy(true); setErr('');
    try {
      await api('/materials/templates', {
        method: 'POST',
        body: JSON.stringify({ name, items: form.items.filter(i => i.description.trim()).map(i => ({ description: i.description, qty: Number(i.expected_qty) || 0, unit: i.unit || null })) }),
      });
      const t = await api('/materials/templates'); setTemplates(t);
      window.alert('Pick list saved.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const markDelivered = async (id) => {
    setBusy(true); setErr('');
    try { await api(`/materials/${id}/deliver`, { method: 'PATCH' }); load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const openReceive = (ship) => {
    setReceiving(ship.id);
    setRecvItems((ship.items || []).map(i => ({ id: i.id, received_qty: i.expected_qty, verified: true })));
    setSignature('');
  };
  const submitReceive = async () => {
    setBusy(true); setErr('');
    try {
      await api(`/materials/${receiving}/receive`, {
        method: 'PATCH',
        body: JSON.stringify({ signature: signature || null, items: recvItems }),
      });
      setReceiving(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // ── Borrows ────────────────────────────────────────────────
  const openBorrow = () => {
    setShowBorrow(true); setBorrowFrom(''); setAvail(null); setPicks({}); setBorrowReason('');
    if (jobs.length === 0) api('/work-items').then(setJobs).catch(() => {});
  };
  const chooseSource = (id) => {
    setBorrowFrom(id); setAvail(null); setPicks({});
    if (!id) return;
    api(`/materials/work-item/${id}/available`).then(rows => {
      setAvail(rows);
      const p = {}; rows.forEach(r => { p[r.description] = { checked: false, qty: '' }; });
      setPicks(p);
    }).catch(e => setErr(e.message));
  };
  const sourceName = () => {
    const j = jobs.find(x => String(x.id) === String(borrowFrom));
    return j ? j.title : 'that job';
  };
  const chosenItems = () => (avail || []).filter(r => picks[r.description]?.checked && Number(picks[r.description]?.qty) > 0);
  const doBorrow = async () => {
    setBusy(true); setErr('');
    try {
      for (const r of chosenItems()) {
        await api('/materials/borrows', {
          method: 'POST',
          body: JSON.stringify({
            from_work_item_id: Number(borrowFrom),
            to_work_item_id: workItemId,
            item: r.description, qty: Number(picks[r.description].qty), unit: r.unit || null,
            reason: borrowReason || null,
          }),
        });
      }
      setConfirmBorrow(false); setShowBorrow(false);
      load();
    } catch (e) { setErr(e.message); setConfirmBorrow(false); } finally { setBusy(false); }
  };
  const toggleBorrowSel = (id) => setSelBorrows(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const openBatchReplenish = () => { setShowReplenish(true); setReplForm({ po_number: '', tracking_number: '' }); };
  const submitReplenishment = async () => {
    setBusy(true); setErr('');
    try {
      const chosen = (data?.borrows || []).filter(b => selBorrows.includes(b.id));
      await api('/materials', {
        method: 'POST',
        body: JSON.stringify({
          work_item_id: workItemId,                 // the robbed job (this one)
          po_number: replForm.po_number || null,
          tracking_number: replForm.tracking_number || null,
          items: chosen.map(b => ({ description: b.item, expected_qty: b.qty, unit: b.unit, replenishes_borrow_id: b.id })),
        }),
      });
      setShowReplenish(false); setSelBorrows([]);
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (loading) return <div style={{ color: '#64748b', fontSize: 13, padding: 20 }}>Loading material…</div>;

  return (
    <div>
      {err && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12, fontWeight: 700 }}>{err}</div>}

      {/* Gate banner */}
      {data?.ready_blocked && (
        <div style={{ backgroundColor: '#3a1212', border: '1px solid #7f1d1d', borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#fca5a5', fontSize: 13 }}>
          ⚠️ <strong>Not ready to work</strong> — {data.awaiting_receipt && 'material not received yet'}
          {data.awaiting_receipt && data.borrowed_short && ' · '}
          {data.borrowed_short && 'material borrowed and not replenished'}.
        </div>
      )}
      {data && !data.ready_blocked && data.shipments.length > 0 && (
        <div style={{ backgroundColor: '#0d2818', border: '1px solid #16632a', borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#4ade80', fontSize: 13 }}>
          ✓ Material received — clear to work.
        </div>
      )}

      {/* Shipments */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 'bold', fontSize: 14, color: '#e2e8f0' }}>Shipments</div>
        {canManage && <button onClick={() => setShowAdd(o => !o)} style={{ fontSize: 12, padding: '5px 11px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>+ Log Shipment</button>}
      </div>

      {showAdd && (
        <div style={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          {templates.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={label}>Start from a pick list (optional)</div>
              <select defaultValue="" onChange={e => { applyTemplate(e.target.value); e.target.value = ''; }} style={inputStyle}>
                <option value="">Load a saved pick list…</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({(t.items || []).length} items)</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div><div style={label}>PO #</div><input style={inputStyle} value={form.po_number} onChange={e => setForm(f => ({ ...f, po_number: e.target.value }))} /></div>
            <div><div style={label}>Tracking #</div><input style={inputStyle} value={form.tracking_number} onChange={e => setForm(f => ({ ...f, tracking_number: e.target.value }))} /></div>
            <div><div style={label}>Carrier</div><input style={inputStyle} value={form.carrier} onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))} /></div>
          </div>
          <div style={label}>Items (the packing list to verify against)</div>
          {form.items.map((it, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: 8, marginBottom: 6 }}>
              <input placeholder="Description" style={inputStyle} value={it.description} onChange={e => setForm(f => { const items = [...f.items]; items[idx] = { ...items[idx], description: e.target.value }; return { ...f, items }; })} />
              <input placeholder="Qty" type="number" style={inputStyle} value={it.expected_qty} onChange={e => setForm(f => { const items = [...f.items]; items[idx] = { ...items[idx], expected_qty: e.target.value }; return { ...f, items }; })} />
              <input placeholder="Unit" style={inputStyle} value={it.unit} onChange={e => setForm(f => { const items = [...f.items]; items[idx] = { ...items[idx], unit: e.target.value }; return { ...f, items }; })} />
              <button onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} style={{ background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 15 }}>✕</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button onClick={() => setForm(f => ({ ...f, items: [...f.items, { description: '', expected_qty: '', unit: '' }] }))} style={{ fontSize: 12, padding: '4px 10px', backgroundColor: '#334155', color: '#94a3b8', border: 'none', borderRadius: 6, cursor: 'pointer' }}>+ Add item</button>
            {canManage && <button onClick={saveTemplate} disabled={busy || !form.items.some(i => i.description.trim())} style={{ fontSize: 12, padding: '4px 10px', backgroundColor: 'transparent', color: '#93c5fd', border: '1px solid #1e3a5f', borderRadius: 6, cursor: 'pointer', opacity: form.items.some(i => i.description.trim()) ? 1 : 0.5 }}>💾 Save as pick list</button>}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowAdd(false)} style={{ padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
            <button onClick={submitShipment} disabled={busy} style={{ padding: '8px 16px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>{busy ? 'Saving…' : 'Log Shipment'}</button>
          </div>
        </div>
      )}

      {data?.shipments.length === 0 && (
        <div style={{ textAlign: 'center', padding: 28, color: '#475569' }}>
          <div style={{ fontSize: 22, marginBottom: 6 }}>📦</div>
          <div style={{ fontSize: 13 }}>No material logged for this job yet</div>
        </div>
      )}

      {data?.shipments.map(s => {
        const badge = STATUS_BADGE[s.status] || STATUS_BADGE.in_transit;
        return (
          <div key={s.id} style={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 14, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 13 }}>
                {s.po_number ? `PO ${s.po_number}` : 'Shipment'} {s.tracking_number && <span style={{ color: '#64748b', fontWeight: 400 }}>· {s.tracking_number}</span>}
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: badge.bg, color: badge.fg }}>{badge.label}</span>
            </div>

            {(s.items || []).length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {s.items.map(i => (
                  <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8', padding: '3px 0', borderBottom: '1px solid #0f172a' }}>
                    <span>{i.description}</span>
                    <span>
                      {i.received_qty != null ? `${i.received_qty}/${i.expected_qty}` : i.expected_qty} {i.unit || ''}
                      {i.verified && <span style={{ color: '#4ade80', marginLeft: 6 }}>✓</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ fontSize: 11, color: '#475569', marginBottom: s.status !== 'received' ? 10 : 0 }}>
              {s.created_by_name ? `Logged by ${s.created_by_name}` : ''}
              {s.received_at && ` · Received by ${s.verified_by_name || '—'} ${new Date(s.received_at).toLocaleDateString()}`}
              {s.signature && ` · signed: ${s.signature}`}
            </div>

            {s.status === 'in_transit' && canManage && (
              <button onClick={() => markDelivered(s.id)} disabled={busy} style={{ padding: '6px 12px', backgroundColor: '#3a2e0a', color: '#fbbf24', border: '1px solid #854d0e', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Mark Delivered</button>
            )}
            {s.status === 'delivered' && receiving !== s.id && (
              <button onClick={() => openReceive(s)} style={{ padding: '6px 12px', backgroundColor: '#14532d', color: '#86efac', border: '1px solid #166534', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Receive &amp; Verify</button>
            )}
            {(s.status === 'delivered' || s.status === 'received') && (s.items || []).length > 0 && (
              <button onClick={() => window.open(`/material-labels/${s.id}`, '_blank')} style={{ marginLeft: 8, padding: '6px 12px', backgroundColor: '#0f1e33', color: '#93c5fd', border: '1px solid #1e3a5f', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>🖨 Print Labels</button>
            )}

            {receiving === s.id && (
              <div style={{ marginTop: 10, backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12 }}>
                <div style={{ ...label, marginBottom: 8 }}>Verify against packing list</div>
                {recvItems.map((ri, idx) => {
                  const orig = s.items.find(x => x.id === ri.id);
                  return (
                    <div key={ri.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ flex: 1, fontSize: 12, color: '#cbd5e1' }}>{orig?.description} <span style={{ color: '#64748b' }}>(exp {orig?.expected_qty} {orig?.unit})</span></span>
                      <input type="number" value={ri.received_qty} onChange={e => setRecvItems(arr => arr.map((x, i) => i === idx ? { ...x, received_qty: e.target.value } : x))} style={{ ...inputStyle, width: 70 }} />
                      <label style={{ fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={ri.verified} onChange={e => setRecvItems(arr => arr.map((x, i) => i === idx ? { ...x, verified: e.target.checked } : x))} /> ok
                      </label>
                    </div>
                  );
                })}
                <div style={{ marginTop: 8 }}>
                  <div style={label}>Signature (your name)</div>
                  <input style={inputStyle} value={signature} onChange={e => setSignature(e.target.value)} placeholder="Type your name to confirm" />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                  <button onClick={() => setReceiving(null)} style={{ padding: '7px 12px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={submitReceive} disabled={busy || !signature.trim()} style={{ padding: '7px 14px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800, opacity: !signature.trim() ? 0.5 : 1 }}>{busy ? 'Saving…' : 'Confirm Received'}</button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Borrows */}
      {(canManage || (data?.borrows.length > 0)) && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 'bold', fontSize: 14, color: '#e2e8f0' }}>Borrows</div>
            {canManage && <button onClick={openBorrow} style={{ fontSize: 12, padding: '5px 11px', backgroundColor: '#7c2d12', color: '#fdba74', border: '1px solid #9a3412', borderRadius: 6, cursor: 'pointer' }}>+ Record Borrow</button>}
          </div>

          {showBorrow && (
            <div style={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 14, marginBottom: 12 }}>
              <div style={{ ...label, marginBottom: 6 }}>Choose the job to borrow material from</div>
              <select value={borrowFrom} onChange={e => chooseSource(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }}>
                <option value="">Select a job…</option>
                {jobs.filter(j => String(j.id) !== String(workItemId)).map(j => (
                  <option key={j.id} value={j.id}>{j.wo_number ? `WO ${j.wo_number} — ` : ''}{j.title}</option>
                ))}
              </select>

              {borrowFrom && avail === null && <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>Checking what's on hand…</div>}
              {borrowFrom && Array.isArray(avail) && avail.length === 0 && (
                <div style={{ color: '#fca5a5', fontSize: 12, marginBottom: 8 }}>That job has no received material on hand to lend.</div>
              )}
              {Array.isArray(avail) && avail.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ ...label, marginBottom: 6 }}>What to borrow</div>
                  {avail.map(r => {
                    const p = picks[r.description] || { checked: false, qty: '' };
                    return (
                      <div key={r.description} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <label style={{ flex: 1, fontSize: 13, color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={p.checked} onChange={e => setPicks(m => ({ ...m, [r.description]: { ...p, checked: e.target.checked, qty: e.target.checked && !p.qty ? r.available : p.qty } }))} />
                          {r.description} <span style={{ color: '#64748b' }}>(available {r.available} {r.unit || ''})</span>
                        </label>
                        {p.checked && (
                          <input type="number" min="0" max={r.available} value={p.qty}
                            onChange={e => setPicks(m => ({ ...m, [r.description]: { ...p, qty: Math.min(Number(e.target.value) || 0, r.available) } }))}
                            style={{ ...inputStyle, width: 80 }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <input placeholder="Reason (optional)" style={{ ...inputStyle, marginBottom: 10 }} value={borrowReason} onChange={e => setBorrowReason(e.target.value)} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowBorrow(false)} style={{ padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => setConfirmBorrow(true)} disabled={chosenItems().length === 0} style={{ padding: '8px 16px', backgroundColor: '#9a3412', color: '#fed7aa', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800, opacity: chosenItems().length === 0 ? 0.5 : 1 }}>Record Borrow</button>
              </div>
            </div>
          )}

          {data?.borrows.length === 0 && !showBorrow && (
            <div style={{ color: '#475569', fontSize: 12 }}>No borrows recorded for this job.</div>
          )}

          {/* Batch replenish bar */}
          {canManage && (data?.borrows || []).some(b => String(b.from_work_item_id) === String(workItemId) && !b.replenished) && (
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={openBatchReplenish} disabled={selBorrows.length === 0}
                style={{ fontSize: 12, padding: '6px 12px', backgroundColor: '#14532d', color: '#86efac', border: '1px solid #166534', borderRadius: 6, cursor: 'pointer', fontWeight: 700, opacity: selBorrows.length === 0 ? 0.5 : 1 }}>
                Replenish Selected ({selBorrows.length})
              </button>
              <span style={{ color: '#64748b', fontSize: 11 }}>check the borrows below — one delivery covers them all</span>
            </div>
          )}

          {showReplenish && (
            <div style={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ ...label, marginBottom: 8 }}>One replenishment delivery for {selBorrows.length} item(s)</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
                {(data?.borrows || []).filter(b => selBorrows.includes(b.id)).map(b => (
                  <div key={b.id}>• {b.qty} {b.unit || ''} {b.item}</div>
                ))}
                <div style={{ marginTop: 6 }}>Must be <strong>received &amp; signed on arrival</strong> — that clears all of them.</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div><div style={label}>PO #</div><input style={inputStyle} value={replForm.po_number} onChange={e => setReplForm(f => ({ ...f, po_number: e.target.value }))} /></div>
                <div><div style={label}>Tracking #</div><input style={inputStyle} value={replForm.tracking_number} onChange={e => setReplForm(f => ({ ...f, tracking_number: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowReplenish(false)} style={{ padding: '7px 12px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                <button onClick={submitReplenishment} disabled={busy} style={{ padding: '7px 14px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>{busy ? 'Saving…' : 'Create Delivery'}</button>
              </div>
            </div>
          )}

          {data?.borrows.map(b => {
            const robbedHere = String(b.from_work_item_id) === String(workItemId);
            const selectable = robbedHere && !b.replenished && canManage;
            return (
              <div key={b.id} style={{ backgroundColor: '#1e293b', border: `1px solid ${b.replenished ? '#334155' : '#7f1d1d'}`, borderRadius: 8, padding: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {selectable && <input type="checkbox" checked={selBorrows.includes(b.id)} onChange={() => toggleBorrowSel(b.id)} />}
                  <div style={{ flex: 1, fontSize: 13, color: '#e2e8f0' }}>
                    {b.qty} {b.unit || ''} {b.item}
                    <span style={{ fontSize: 12, color: robbedHere ? '#fca5a5' : '#86efac', marginLeft: 8 }}>
                      {robbedHere ? `→ lent to: ${b.to_work_item_title || 'another job'}` : `← borrowed from: ${b.from_work_item_title || 'another job'}`}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 3, marginLeft: selectable ? 24 : 0 }}>
                  {b.borrowed_by_name || '—'} · {new Date(b.borrowed_at).toLocaleDateString()}
                  {b.reason && ` · ${b.reason}`}
                  {b.replenished ? <span style={{ color: '#4ade80' }}> · replenished ✓</span> : <span style={{ color: '#fca5a5' }}> · needs replenish</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Borrow confirmation */}
      {confirmBorrow && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 460, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 22 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 17, marginBottom: 8 }}>Confirm borrow</div>
            <div style={{ color: '#cbd5e1', fontSize: 14, marginBottom: 12 }}>
              Borrow the selected material from <strong style={{ color: '#fdba74' }}>{sourceName()}</strong>?
            </div>
            <div style={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, padding: 10, marginBottom: 12 }}>
              {chosenItems().map(r => (
                <div key={r.description} style={{ fontSize: 13, color: '#e2e8f0' }}>• {picks[r.description].qty} {r.unit || ''} {r.description}</div>
              ))}
            </div>
            <div style={{ color: '#fca5a5', fontSize: 13, marginBottom: 18 }}>
              ⚠️ This moves <strong>{sourceName()}</strong> to <strong>Not Ready for Install</strong> until it's replenished.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmBorrow(false)} style={{ padding: '9px 14px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={doBorrow} disabled={busy} style={{ padding: '9px 18px', backgroundColor: '#9a3412', color: '#fed7aa', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>{busy ? 'Saving…' : 'Yes, Borrow'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
